import React, { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Sparkles, RefreshCw, Copy, CheckCheck, Download, AlertTriangle } from "lucide-react";
import { useAdminAuth } from "../AdminAuthContext";
import { streamAnalysis } from "../adminApi";
import { Button } from "../../components/shared/Button";

function fmtAgo(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const ms = Date.now() - d.getTime();
        const sec = Math.floor(ms / 1000);
        if (sec < 60) return `${sec}s ago`;
        const min = Math.floor(sec / 60);
        if (min < 60) return `${min}m ago`;
        const hr = Math.floor(min / 60);
        if (hr < 24) return `${hr}h ago`;
        return d.toLocaleDateString(undefined, { month: "short", day: "2-digit" });
    } catch {
        return "";
    }
}

export default function AdminAnalysis() {
    const { token } = useAdminAuth();
    const { includeSeed, refreshKey } = useOutletContext();
    const [text, setText] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [err, setErr] = useState(null);
    const [meta, setMeta] = useState({
        cached: false,
        generatedAt: null,
        total: 0,
    });
    const [startedAt, setStartedAt] = useState(null);
    const [elapsed, setElapsed] = useState(0);
    const [copied, setCopied] = useState(false);
    const abortRef = useRef(null);

    function start({ force = false } = {}) {
        setText("");
        setErr(null);
        setStartedAt(Date.now());
        setStreaming(true);
        setMeta({ cached: false, generatedAt: null, total: 0 });
        abortRef.current = streamAnalysis({
            token,
            includeSeed,
            force,
            onText: (chunk) => setText((prev) => prev + chunk),
            onDone: (info) => {
                setStreaming(false);
                setElapsed(Math.round((Date.now() - startedAtRef.current) / 1000));
                setMeta({
                    cached: Boolean(info.cached),
                    generatedAt: info.generated_at || null,
                    total: info.total || 0,
                });
            },
            onError: (e) => {
                setErr(typeof e === "string" ? e : "Analysis failed.");
                setStreaming(false);
            },
        });
    }

    // mirror startedAt into a ref the closure above can read on done
    const startedAtRef = useRef(null);
    useEffect(() => {
        startedAtRef.current = startedAt;
    }, [startedAt]);

    // tick elapsed counter while streaming
    useEffect(() => {
        if (!streaming || !startedAt) return undefined;
        const id = setInterval(() => {
            setElapsed(Math.round((Date.now() - startedAt) / 1000));
        }, 250);
        return () => clearInterval(id);
    }, [streaming, startedAt]);

    useEffect(() => {
        return () => {
            if (abortRef.current) abortRef.current();
        };
    }, []);

    function copy() {
        navigator.clipboard?.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    }

    function download() {
        const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `klareet_analysis_${new Date().toISOString().slice(0, 10)}.txt`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }

    return (
        <div className="space-y-6" data-testid="admin-analysis">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-2xl font-semibold tracking-tight text-klareet-TEXT flex items-center gap-2">
                        <Sparkles className="text-klareet-T2" size={20} />
                        Strategic Analysis
                    </h2>
                    <p className="mt-1 text-sm text-klareet-MUT max-w-prose">
                        Claude Sonnet 4 reads your data and returns 4 paragraphs
                        of strategic insight.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {!streaming && !text ? (
                        <Button
                            size="md"
                            onClick={() => start({ force: false })}
                            data-testid="generate-analysis-btn"
                        >
                            <Sparkles size={16} />
                            Generate analysis
                        </Button>
                    ) : (
                        <Button
                            size="md"
                            variant="outline"
                            onClick={() => start({ force: true })}
                            disabled={streaming}
                            data-testid="regenerate-btn"
                        >
                            <RefreshCw size={16} />
                            Regenerate
                        </Button>
                    )}
                </div>
            </div>

            {/* Metadata strip */}
            <div className="text-xs font-mono text-klareet-MUT flex flex-wrap gap-x-4 gap-y-1">
                {meta.cached && meta.generatedAt ? (
                    <span data-testid="cached-meta">
                        Cached from {fmtAgo(meta.generatedAt)}
                    </span>
                ) : null}
                {streaming ? (
                    <span data-testid="streaming-meta">
                        Streaming · {elapsed}s elapsed · {text.length} chars
                    </span>
                ) : null}
                {!streaming && text ? (
                    <span data-testid="done-meta">
                        Generated · {elapsed}s · {text.length} chars
                    </span>
                ) : null}
            </div>

            {err ? (
                <div className="rounded-2xl border border-klareet-RED/40 bg-klareet-RED/5 p-5 flex items-start gap-3">
                    <AlertTriangle className="text-klareet-RED shrink-0 mt-0.5" size={18} />
                    <div className="flex-1">
                        <div className="text-klareet-RED font-medium">{err}</div>
                        <div className="mt-3">
                            <Button size="sm" onClick={() => start({ force: true })}>
                                Retry
                            </Button>
                        </div>
                    </div>
                </div>
            ) : null}

            <div
                className="rounded-2xl border border-klareet-bor bg-klareet-card p-6 md:p-8 min-h-[420px]"
                data-testid="analysis-panel"
            >
                {!text && !streaming ? (
                    <div className="text-klareet-MUT text-sm">
                        No analysis generated yet for this dataset. Click
                        <span className="text-klareet-TEXT"> Generate analysis </span>
                        above to stream a fresh strategic read from Claude.
                    </div>
                ) : (
                    <article
                        className="prose-klareet text-klareet-TEXT leading-relaxed whitespace-pre-wrap text-[15px] md:text-base"
                        data-testid="analysis-text"
                    >
                        {renderMarkdownLite(text)}
                        {streaming ? (
                            <span className="inline-block w-2 h-5 align-middle bg-klareet-T2 ml-0.5 animate-pulse" />
                        ) : null}
                    </article>
                )}
            </div>

            {!streaming && text ? (
                <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" onClick={copy} data-testid="copy-analysis-btn">
                        {copied ? <CheckCheck size={14} /> : <Copy size={14} />}
                        {copied ? "Copied" : "Copy to clipboard"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={download} data-testid="download-analysis-btn">
                        <Download size={14} />
                        Download .txt
                    </Button>
                </div>
            ) : null}
        </div>
    );
}

/**
 * Tiny markdown renderer: only `**bold**` and paragraph breaks.
 * Anthropic returns minimal markdown; this is enough to make the
 * paragraph headings stand out without pulling a parser library.
 */
function renderMarkdownLite(text) {
    if (!text) return null;
    const blocks = text.split(/\n{2,}/);
    return blocks.map((block, i) => (
        <p key={i} className="mb-4">
            {renderInline(block)}
        </p>
    ));
}

function renderInline(line) {
    const parts = [];
    const re = /\*\*(.+?)\*\*/g;
    let last = 0;
    let m;
    let key = 0;
    while ((m = re.exec(line)) !== null) {
        if (m.index > last) {
            parts.push(line.slice(last, m.index));
        }
        parts.push(
            <strong key={`b${key++}`} className="text-klareet-T2 font-semibold">
                {m[1]}
            </strong>,
        );
        last = m.index + m[0].length;
    }
    if (last < line.length) {
        parts.push(line.slice(last));
    }
    return parts.length ? parts : line;
}
